import React, { useState } from "react";

// CanaryPanel — admin-only "Run 5-Draft Canary" for Perkins Phase 2b
// (produce-then-schedule). Lives inside the authenticated SEO dashboard, so it
// uses the dashboard's existing trusted auth path (authFetch → the logged-in
// staff member's Supabase session). It:
//   1) shows the dry-run PLAN first (no generation, no mutation),
//   2) requires an explicit "Run 5-draft canary" confirmation,
//   3) produces 5 DRAFTS only — never publishes or schedules,
//   4) polls Perkins and reports drafted / held(+reasons) / skipped / failed
//      and an explicit "0 published" proof.
//
// Props: API (backend base url), authFetch (session-authenticated fetch), client.

const RED = "#d60000";
const MUTED = "#666";
const CARD = "#0a0a0a";
const LINE = "#1a1a1a";
const CAP = 5;

const box = { background: CARD, border: `1px solid ${LINE}`, borderRadius: 4, padding: 16 };
const label = { fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", color: MUTED, fontFamily: "'Barlow Condensed', sans-serif" };
const btn = (on) => ({ padding: "9px 20px", background: on ? RED : "#141414", border: `1px solid ${on ? RED : "#2a2a2a"}`, color: on ? "#fff" : "#444", cursor: on ? "pointer" : "not-allowed", fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", fontWeight: 700, fontFamily: "'Barlow Condensed', sans-serif", borderRadius: 2 });

function currentMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const STATE_COLOR = { drafted: "#22c55e", held: "#e0a11f", skipped: "#3aa0e0", failed: RED, planned: MUTED, published: RED };

export default function CanaryPanel({ API, authFetch, client }) {
  const [month, setMonth] = useState(currentMonth());
  const [phase, setPhase] = useState("idle"); // idle | previewed | confirm | running | done
  const [plan, setPlan] = useState(null);
  const [canaryKeywords, setCanaryKeywords] = useState([]);
  const [rows, setRows] = useState([]); // per-keyword live state during/after run
  const [publishedCount, setPublishedCount] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  if (!client) return <div style={{ color: MUTED, fontSize: 12 }}>Select a client to run a production canary.</div>;

  const post = async (body) => {
    const res = await authFetch(`${API}/api/perkins/clients/${client.id}/produce/${month}`, {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
    return json;
  };
  const getBatch = async () => {
    const res = await authFetch(`${API}/api/perkins/clients/${client.id}/batches/${month}`);
    const json = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
    return json;
  };

  // Step 1 — dry-run preview (no generation).
  const preview = async () => {
    setError(null); setBusy(true); setPlan(null); setRows([]); setPublishedCount(null);
    try {
      const p = await post({ cap: CAP, dryRun: true });
      const would = (p.results || []).filter((r) => r.outcome === "would_produce").map((r) => r.keyword);
      const skipped = (p.results || []).filter((r) => r.outcome === "skipped");
      setPlan({ plannedTotal: p.plannedTotal, attempted: p.attempted, would, skipped });
      setCanaryKeywords(would.slice(0, CAP));
      setPhase("previewed");
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  // Step 3 — run the canary (after explicit confirm), then poll Perkins.
  const run = async () => {
    setError(null); setBusy(true); setPhase("running");
    const watch = canaryKeywords.slice();
    setRows(watch.map((kw) => ({ keyword: kw, state: "planned", holdReason: null })));
    try {
      await post({ cap: CAP }); // 202 accepted, background generation, drafts only
      // Poll the Perkins batch until every canary keyword resolves off "planned".
      let published = 0;
      for (let i = 0; i < 60; i++) {
        await sleep(10000);
        const batch = await getBatch().catch(() => null);
        if (!batch) continue;
        const byKw = new Map((batch.articles || []).map((a) => [a.keyword, a]));
        const next = watch.map((kw) => {
          const a = byKw.get(kw);
          return { keyword: kw, state: a ? a.state : "planned", holdReason: a ? a.holdReason : null };
        });
        setRows(next);
        published = next.filter((r) => r.state === "published").length;
        setPublishedCount(published);
        if (next.every((r) => r.state !== "planned")) break;
      }
      setPhase("done");
    } catch (e) { setError(e.message); setPhase("done"); }
    finally { setBusy(false); }
  };

  const tally = (st) => rows.filter((r) => r.state === st).length;

  return (
    <div style={{ paddingTop: 20, maxWidth: 720, display: "flex", flexDirection: "column", gap: 14 }}>
      <div>
        <div style={{ fontSize: 16, fontFamily: "'Oswald', sans-serif", textTransform: "uppercase", letterSpacing: "0.06em" }}>
          Produce Canary <span style={{ color: RED }}>— {client.name}</span>
        </div>
        <div style={{ fontSize: 12, color: MUTED, marginTop: 4 }}>
          Pre-generates a small batch of <b style={{ color: "#ccc" }}>{CAP} drafts</b> to validate produce-then-schedule.
          It never publishes or schedules. Preview the plan, then confirm.
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "flex-end", gap: 14 }}>
        <div>
          <div style={label}>Client</div>
          <div style={{ fontSize: 13, color: "#ddd", marginTop: 4 }}>{client.name}</div>
        </div>
        <div>
          <div style={label}>Month</div>
          <input type="month" value={month} onChange={(e) => { setMonth(e.target.value); setPhase("idle"); setPlan(null); }}
            style={{ marginTop: 4, background: "#000", border: `1px solid #2a2a2a`, color: "#fff", padding: "7px 10px", fontSize: 12, borderRadius: 2, colorScheme: "dark" }} />
        </div>
        <button onClick={preview} disabled={busy} style={btn(!busy)}>{busy && phase === "idle" ? "Loading…" : "Preview plan (dry run)"}</button>
      </div>

      {error && <div style={{ ...box, borderColor: RED, color: "#ff8a80", fontSize: 12 }}>Error: {error}</div>}

      {plan && (
        <div style={box}>
          <div style={label}>Plan (dry run — nothing generated)</div>
          <div style={{ fontSize: 13, marginTop: 8, color: "#ddd" }}>
            <b style={{ color: "#fff" }}>{plan.plannedTotal}</b> planned this month · canary will produce{" "}
            <b style={{ color: RED }}>{Math.min(CAP, canaryKeywords.length)}</b>
            {plan.skipped.length ? <> · <b style={{ color: "#3aa0e0" }}>{plan.skipped.length}</b> already have drafts (skipped)</> : null}
          </div>
          <ol style={{ margin: "10px 0 0", paddingLeft: 20, fontSize: 12, color: "#bbb", lineHeight: 1.7 }}>
            {canaryKeywords.map((kw) => <li key={kw}>{kw}</li>)}
          </ol>
          {phase === "previewed" && (
            <button onClick={() => setPhase("confirm")} style={{ ...btn(true), marginTop: 14 }}>Run {CAP}-draft canary</button>
          )}
        </div>
      )}

      {phase === "confirm" && (
        <div style={{ ...box, borderColor: RED }}>
          <div style={{ fontSize: 13, color: "#eee" }}>
            This generates <b>{Math.min(CAP, canaryKeywords.length)} drafts</b> for <b>{client.name}</b> ({month}).
            <br />No post will be published and nothing will be scheduled.
          </div>
          <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
            <button onClick={run} disabled={busy} style={btn(!busy)}>Confirm: Run {CAP}-draft canary</button>
            <button onClick={() => setPhase("previewed")} disabled={busy} style={{ ...btn(true), background: "#141414", border: "1px solid #2a2a2a", color: "#999" }}>Cancel</button>
          </div>
        </div>
      )}

      {(phase === "running" || phase === "done") && (
        <div style={box}>
          <div style={label}>{phase === "running" ? "Producing… (drafts only, live)" : "Canary report"}</div>
          <div style={{ display: "flex", gap: 18, margin: "10px 0 12px", fontSize: 12, flexWrap: "wrap" }}>
            <span>drafted <b style={{ color: STATE_COLOR.drafted }}>{tally("drafted")}</b></span>
            <span>held <b style={{ color: STATE_COLOR.held }}>{tally("held")}</b></span>
            <span>skipped <b style={{ color: STATE_COLOR.skipped }}>{tally("skipped")}</b></span>
            <span>failed <b style={{ color: STATE_COLOR.failed }}>{tally("failed")}</b></span>
            <span>planned <b style={{ color: MUTED }}>{tally("planned")}</b></span>
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <tbody>
              {rows.map((r) => (
                <tr key={r.keyword} style={{ borderTop: `1px solid ${LINE}` }}>
                  <td style={{ padding: "7px 8px 7px 0", color: "#ccc" }}>{r.keyword}</td>
                  <td style={{ padding: "7px 0", textAlign: "right", whiteSpace: "nowrap" }}>
                    <span style={{ color: STATE_COLOR[r.state] || MUTED, textTransform: "uppercase", fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: "0.08em" }}>
                      {r.state === "planned" ? (phase === "running" ? "producing…" : "pending") : r.state}
                    </span>
                    {r.holdReason ? <span style={{ color: MUTED }}> · {r.holdReason}</span> : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {phase === "done" && (
            <div style={{ marginTop: 14, padding: "10px 12px", borderRadius: 3,
              background: publishedCount === 0 ? "rgba(34,197,94,0.08)" : "rgba(214,0,0,0.1)",
              border: `1px solid ${publishedCount === 0 ? "rgba(34,197,94,0.4)" : RED}`,
              color: publishedCount === 0 ? "#8be9a0" : "#ff8a80", fontSize: 12, fontWeight: 700 }}>
              {publishedCount === 0
                ? "✓ 0 published — the canary produced drafts only, nothing went live or was scheduled."
                : `⚠ ${publishedCount} published — unexpected; the producer should never publish. Investigate.`}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
